<template>
  <div v-tooltip="`${sourceName} <br> Search: ${searchQuery}`">
    <template v-if="iconUrl">
      <img
        v-show="isIconValid"
        :key="iconUrl"
        class="w-5 h-5"
        :src="iconUrl"
        alt="source logo"
        @load="({ target }) => checkImage(target as HTMLImageElement)"
        @error="({ target }) => handleImageError(target as HTMLImageElement)"
      />
      <div v-show="!isIconValid" class="flex">
        <span
          class="w-6 h-6 p-0.5 border-primary/50 border-2 text-primary/80 rounded-full"
        >
          <DatabaseIcon class="w-4 h-4" />
        </span>
      </div>
    </template>
    <div v-else class="flex">
      <span
        class="w-6 h-6 p-0.5 border-primary/50 border-2 text-primary/80 rounded-full"
      >
        <DatabaseIcon class="w-4 h-4" />
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import DatabaseIcon from '@app/components/Icons/DatabaseIcon.vue'
import { ReviewItem } from '@core/domain/models/reviewItem.model'
import { ReviewItemType } from '@core/domain/types/reviewItemType.type'
import { isUrlValid } from '@app/utils/urlValidation'
import { injectStrict } from '@app/utils/injectStrict'
import { SafetyInformationSourcesKey } from '@app/injectionKeys'
import { FieldSafetyNotice } from '@core/domain/models/fieldSafetyNotice.model'

const props = defineProps<{
  reviewItem: ReviewItem
}>()

const isIconValid = ref(true)

const safetyInformationSources = injectStrict(SafetyInformationSourcesKey)

const iconUrl = computed(() => {
  if (props.reviewItem.type === ReviewItemType.FieldSafetyNotice) {
    const fsn = props.reviewItem as FieldSafetyNotice
    return safetyInformationSources.find(
      (source) => source.id === fsn.search?.source.id,
    )?.country.flagUrl
  }

  const sourceUrl = props.reviewItem.search?.source.url ?? ''
  if (isUrlValid(sourceUrl)) {
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(sourceUrl)}&sz=64`
  }
  return ''
})

const sourceName = computed(() => props.reviewItem.search?.source.name ?? '')
const searchQuery = computed(() => props.reviewItem.search?.searchQuery ?? '')

function checkImage(target: HTMLImageElement) {
  if (target.naturalWidth === 5 && target.naturalHeight === 5) {
    isIconValid.value = false
  } else {
    isIconValid.value = true
  }
}

function handleImageError(target: HTMLImageElement) {
  target.src = '/sources-icons/custom-source.png'
}
</script>
